import { takeLatest, put, call, takeEvery, select } from 'redux-saga/effects';
import * as WorkspaceProxy from '../../proxies/WorkspaceProxy';
import * as ProjectProxy from '../../proxies/ProjectProxy';
import * as ProjectionProxy from '../../proxies/ProjectionProxy';
import * as WorkspaceVariablesProxy from '../../proxies/WorkspaceVariablesProxy';
import * as WorkspaceGeometriesProxy from '../../proxies/WorkspaceGeometriesProxy';
import * as WorkspaceQueryProxy from '../../proxies/WorkspaceQueryProxy';
import { IAction } from '../../store/actions/Action';
import { WorkspaceActionType } from '../store/WokspaceActionType';
import { t } from '../../translations/i18n';
import { IResponse } from '../../models/IResponse';
import { IWorkspace, IWorkspaceOverview } from '../../models/IWorkspaces';
import { IGlobalState } from '../../store/reducers';
import { getRouteByPath, ROUTES } from '../../app/routes';
import * as ProjectContentProxy from '../../proxies/ProjectContentProxy';
import { mapDatasets } from '../../managers/WorkspaceManager';
import { IProjection } from '../../models/IProjections';
import { EMapToolActionType } from '../../store/actions/MapToolActionType';
import { EItemType } from '../../models/IOperationDescriptions';
import {
  clearFeaturesForEditing,
  createGeoemtryFailed,
  createVariableFailed,
  creatingGeometry,
  creatingVariable,
  setLoadingFeaturesForEditing,
  updateGeometryDataFailed,
  updateVariableDataFailed,
  updatingGeometryData,
  updatingVariableData,
} from '../../store/actions/editingItems';
import { HttpErrorHandler } from '../../managers/http-utils';
import { EWorkspaceVariableActionType } from '../../store/actions/WorkspaceVariableActionType';
import { EWorkspaceActionType } from '../../store/actions/WorkspaceActionType';
import { USAGE_TYPES } from '../../project-datasets/mesh-project-dataset-constants';
import { ISelectionQueryApi, QUERY_RELATIONS } from '../../models/IQueryDefinitions';
import { IOperationMetadata } from '../../models/IOperations';
import { EWorkspaceGeometryActionType } from '../../store/actions/WorkspaceGeometryActionType';
import { EGeometryItemTypes } from '../../models/IGeometries';
import { IChangedNode } from '../../store/reducers/MeshMapToolReducer';
import { IPatchFeature } from '../../models/IQueryCompleted';
import { changedNodesToNodePatchFeatures, previousEditsToPatchFeatures } from './meshEditHelpers';
import { NavigateFunction } from 'react-router-dom';
import { getNodesToUpdate } from '../../meshes/sagas/editMeshSagas.ts';
import { getProjectPath } from '../../store/actions/project.ts';
import { getWorkspace } from '../../store/selectors/WorkspaceSelectors.ts';
import { getUnitFromWKT } from '../../meshes/queries/mesh-queries-utils.ts';

const getBreadcrumps = (state: IGlobalState) => state.AppReducer.breadcrumbs;

/**
 * Defines sagas for workspace CRUD operations
 */
export default function* watchWorkspaceEdit() {
  yield takeLatest(WorkspaceActionType.WORKSPACE_LOAD_OVERVIEW, loadWorkspaceOverview);
  yield takeLatest(WorkspaceActionType.WORKSPACE_LOAD_METADATA, loadWorkspaceMetadata);
  yield takeLatest(WorkspaceActionType.LOAD_WORKSPACE, loadWorkspace);
  yield takeEvery(WorkspaceActionType.UPDATE_WORKSPACE, updateWorkspace);
  yield takeLatest(WorkspaceActionType.WORKSPACE_ADD_PROJ4STRING, enrichWorkspaceWithProj4String);
  yield takeLatest(EMapToolActionType.GET_FEATURES_FOR_EDITING, handleGetFeaturesForEditing);
  yield takeEvery(EWorkspaceVariableActionType.UPDATE_DATA, handleUpdateVariableData);
  yield takeEvery(EWorkspaceVariableActionType.CREATE, handleCreateVariable);
  yield takeEvery(EWorkspaceGeometryActionType.UPDATE, handleUpdateGeometryData);
  yield takeEvery(EWorkspaceGeometryActionType.CREATE, handleCreateGeometry);
}

function* handleCreateVariable(action: IAction) {
  const { workspaceId, previousEdits } = action.data;
  yield put({ type: 'toast/ADD/SUCCESS', toast: { text: t('VARIABLE_DRAWING_SUBMITTED_SUCCESSFULLY') } });
  yield put(creatingVariable());
  try {
    yield call(
      WorkspaceVariablesProxy.createVariableDataWithCachedEdits,
      workspaceId,
      t('SCATTER'),
      USAGE_TYPES.VARIABLE,
      previousEdits,   
    );
    yield put(clearFeaturesForEditing());
    yield put({ type: EMapToolActionType.VARIABLES_UNLOAD });
    yield put({ type: EMapToolActionType.DISABLE_ALL_DRAWING_TOOLS });
    yield put({ type: EWorkspaceActionType.EXIT_ACTIVE_PANEL });
  } catch (error: any) {
    yield put(createVariableFailed());
    yield put({ type: 'toast/ADD/ERROR', toast: { text: t('VARIABLE_DRAWING_SUBMIT_FAILED') } });
    HttpErrorHandler('Failed to update variable data with geojson.', error);
  } finally {
    yield put(creatingVariable(false));
  }
}

function* handleUpdateVariableData(action: IAction) {
  const { workspaceId, variableId, previousEdits } = action.data;
  yield put({ type: 'toast/ADD/SUCCESS', toast: { text: t('VARIABLE_EDIT_UPDATE_SUBMITTED') } });
  yield put(updatingVariableData());
  try {
    yield call(WorkspaceVariablesProxy.updateVariableDataWithcachedEdits, workspaceId, variableId, previousEdits);
    yield put(clearFeaturesForEditing());
    yield put({ type: EMapToolActionType.VARIABLES_UNLOAD });
    yield put({ type: EMapToolActionType.DISABLE_ALL_DRAWING_TOOLS });
    yield put({ type: EWorkspaceActionType.EXIT_ACTIVE_PANEL });
  } catch (error: any) {
    yield put(updateVariableDataFailed());
    yield put({ type: 'toast/ADD/ERROR', toast: { text: t('VARIABLE_EDIT_UPDATE_FAILED') } });
    HttpErrorHandler('Failed to update variable data with geojson.', error);
  } finally {
    yield put(updatingVariableData(false));
  }
}

function* handleCreateGeometry(action: IAction) {
  const { workspaceId, previousEdits } = action.data;
  yield put({ type: 'toast/ADD/SUCCESS', toast: { text: t('GEOMETRY_DRAWING_SUBMITTED_SUCCESSFULLY') } });
  yield put(creatingGeometry());
  try {
    yield call(WorkspaceGeometriesProxy.createGeometryDataWithCachedEdits, workspaceId, t('GEOMETRY'), previousEdits);
    yield put(clearFeaturesForEditing());
    yield put({ type: EMapToolActionType.VARIABLES_UNLOAD });
    yield put({ type: EMapToolActionType.DISABLE_ALL_DRAWING_TOOLS });
    yield put({ type: EWorkspaceActionType.EXIT_ACTIVE_PANEL });
  } catch (error: any) {
    yield put(createGeoemtryFailed());
    yield put({ type: 'toast/ADD/ERROR', toast: { text: t('GEOMETRY_DRAWING_SUBMIT_FAILED') } });
    HttpErrorHandler('Failed to update geometry data with geojson.', error);
  } finally {
    yield put(creatingGeometry(false));
  }
}

function* handleUpdateGeometryData(action: IAction) {
  const { workspaceId, geometryId, previousEdits } = action.data;
  yield put({ type: 'toast/ADD/SUCCESS', toast: { text: t('GEOMETRY_EDIT_UPDATE_SUBMITTED') } });
  yield put(updatingGeometryData());
  try {
    yield call(WorkspaceGeometriesProxy.updateGeometryDataWithcachedEdits, workspaceId, geometryId, previousEdits);
    yield put(clearFeaturesForEditing());
    yield put({ type: EMapToolActionType.DISABLE_ALL_DRAWING_TOOLS });
    yield put({ type: EWorkspaceActionType.EXIT_ACTIVE_PANEL });
  } catch (error: any) {
    yield put(updateGeometryDataFailed());
    yield put({ type: 'toast/ADD/ERROR', toast: { text: t('GEOMETRY_EDIT_UPDATE_FAILED') } });
    HttpErrorHandler('Failed to update geometry data with geojson.', error);
  } finally {
    yield put(updatingGeometryData(false));
  }
}

function* handleGetFeaturesForEditing(action: IAction) {
  const { workspaceId, itemId, itemType, selectionFeatureCollection, previousEdits, geometryEditType } = action.data;
  yield put(setLoadingFeaturesForEditing());

  const relationType =
    itemType === EItemType.MESH
      ? QUERY_RELATIONS.NEAREST
      : geometryEditType === EGeometryItemTypes.POINT
        ? QUERY_RELATIONS.ISWITHIN
        : QUERY_RELATIONS.INTERSECTS;

  const resType = itemType === EItemType.MESH ? 'nodePatch' : 'geometry'; //EQueryResultTypes.NODEPATCH : EQueryResultTypes.GEOMETRY;
  const resReturnType = 'featureDtoCollection';
  const payload: ISelectionQueryApi = {
    type: 'SpatialQueryDefinition2',
    relation: relationType, // relation of target geometries to selection geometry
    featureCollection: selectionFeatureCollection,
    targetItems: [itemId],
    resultReturnType: resReturnType,
    resultType: resType,
  };

  if (itemType === EItemType.MESH) {
    const previousNodesToUpdate: Array<IChangedNode> = yield select(getNodesToUpdate);
    const nodePatchFeatures: Array<IPatchFeature> = changedNodesToNodePatchFeatures(previousNodesToUpdate);
    payload.editedFeatures = nodePatchFeatures;
  } else {
    const patchFeatures: Array<IPatchFeature> = previousEditsToPatchFeatures(previousEdits);
    payload.editedFeatures = patchFeatures;
  }
  try {
    const response: { data: IOperationMetadata } = yield call(
      WorkspaceQueryProxy.createSelectionQuery,
      workspaceId,
      payload,
    );

    if (response.data && response.data.id) {
      yield put({
        type: EMapToolActionType.SET_FEATURES_FOR_EDITING_IDS,
        data: { operationId: response.data.id, targetId: itemId },
      });
    }
  } catch {
    yield put({ type: EMapToolActionType.LOADING_FEATURES_FOR_EDITING_FAILED });
  }
}

function* loadWorkspaceOverview(action: IAction) {
  const projectId = action.data.projectId;
  const navigate: NavigateFunction = action.data.navigate;
  let project = null;
  if (projectId) {
    yield put({ type: 'workspaces/LIST' });
    try {
      const response: IResponse = yield call(ProjectContentProxy.getProject, projectId);
      if ((response.status === 200 || response.status === 201) && response.data) {
        project = response.data;
        yield put({ type: 'project/LOAD_SUCCESS', project });
      }
    } catch {      
      navigate(ROUTES.notFound.path);
    }
    if (project) {
      const workspace = yield select(getWorkspace)
      yield put(getProjectPath(projectId, workspace));
      try {
        const response: IResponse = yield call(WorkspaceProxy.getWorkspaceOverviewList, projectId, true);
        if ((response.status === 200 || response.status === 201) && response.data) {
          const workspaces: Array<IWorkspaceOverview> = response.data;
          yield put({
            type: 'workspaces/LIST_SUCCESS',
            workspaces,
          });
        }
        const subProjectsResponse = yield call(ProjectProxy.getSubprojectsList, projectId);
        if ((subProjectsResponse.status === 200 || subProjectsResponse.status === 201) && subProjectsResponse.data) {
          const subprojects = subProjectsResponse.data;
          yield put({
            type: 'project/SUBPROJECT_LIST_SUCCES',
            subprojects,
          });
        }
      } catch {
        navigate(ROUTES.notFound.path);
      }
    }
  }
}

function* enrichWorkspaceWithProj4String(action) {
  const { workspace } = action;
  let enrichedWorkspace = workspace;
  if (workspace.epsgCode) {
    try{
      const projectionResponse = yield call(ProjectionProxy.getProjectionByEpsg, workspace.epsgCode);
      if (projectionResponse && projectionResponse.data) {
        const projection: IProjection = projectionResponse.data;
        if (projection){
          if (projection.proj4String) {
              enrichedWorkspace = { ...workspace, proj4String: projection.proj4String };
          }
          if (workspace.isLatLon){
            enrichedWorkspace = { ...enrichedWorkspace, unit: "deg" };
          }
          else if (projection.wkt){
            const unit = yield call(getUnitFromWKT, projection.wkt);
            enrichedWorkspace = { ...enrichedWorkspace, unit };
          } 
        }        
      }
    }
    catch(error){
      console.log(error)
    }
    
  }
  yield put({
    type: WorkspaceActionType.WORKSPACE_LOAD_SUCCESS,
    workspace: enrichedWorkspace,
  });
}

function* loadWorkspaceMetadata(action: IAction) {
  const projectId = action.data.pId;
  const workspaceId = action.data.wsId;
  const navigate: NavigateFunction = action.data.navigate;
  let project = null;
  if (projectId && workspaceId) {
    yield put({ type: WorkspaceActionType.WORKSPACE_LOADING });
    try {
      const response: IResponse = yield call(ProjectContentProxy.getProject, projectId);
      if ((response.status === 200 || response.status === 201) && response.data) {
        project = response.data;
        yield put({ type: 'project/LOAD_SUCCESS', project });
      }
    } catch {
      navigate(ROUTES.notFound.path); 
    }

    if (project) {
      const workspace = yield select(getWorkspace)
      yield put(getProjectPath(projectId, workspace));
      try {
        const response: IResponse = yield call(WorkspaceProxy.getWorkspaceOverview, workspaceId);
        if ((response.status === 200 || response.status === 201) && response.data) {
          const workspace: IWorkspaceOverview = response.data;
          yield put({ type: WorkspaceActionType.WORKSPACE_ADD_PROJ4STRING, workspace });
          const recentWs = { id: workspaceId, epsgCode: workspace.epsgCode, name: workspace.name };
          yield put({ type: 'workspace/recent/ADD', recentWs });
        }
      } catch {
        navigate(ROUTES.notFound.path);
      }
    }
  }
}

/**
 * Generator function for loading workspace
 * @param action
 */
function* loadWorkspace(action: IAction) {
  const workspaceId = action.data.workspaceId;
  yield put({ type: WorkspaceActionType.WORKSPACE_LOADING });
  yield put({ type: WorkspaceActionType.WORKSPACE_UPDATING, updating: true });
  try {
    const response: IResponse = yield call(WorkspaceProxy.getWorkspace, workspaceId);

    if ((response.status === 200 || response.status === 201) && response.data) {
      const workspace: IWorkspace = response.data;
      yield put({ type: WorkspaceActionType.WORKSPACE_ADD_PROJ4STRING, workspace });
    }
  } catch {
    const navigate: NavigateFunction = action.data.navigate;
    navigate(ROUTES.notFound.path);
  } finally {
    yield put({ type: WorkspaceActionType.WORKSPACE_UPDATING, updating: false });
  }
}

/**
 * Generator function for updating workspace
 * @param action
 */
function* updateWorkspace(action: IAction) {
  const breadcrumbs = yield select(getBreadcrumps);
  const { workspace, navigateToWorkspacePanel } = action.data;
  const workspaceId = workspace.id;
  const projectId = workspace.projectId;
  yield put({ type: WorkspaceActionType.WORKSPACE_UPDATING, updating: true });

  try {
    const data = mapDatasets(workspace.datasets);
    const response: IResponse = yield call(WorkspaceProxy.updateWorkspace, workspace.id, {
      ...workspace,
      datasets: data,
    });

    if ((response.status === 200 || response.status === 201 || response.status === 202) && response.data) {
      yield put({ type: WorkspaceActionType.LOAD_WORKSPACE, data: { workspaceId } });
      const toast = { text: t('UPDATED', 1, { thing: workspace.name }) };
      yield put({ type: 'toast/ADD/SUCCESS', toast });
    }

    if (navigateToWorkspacePanel) {
      // after ws update success we update the crumbs as well
      let newBreadcrumbs = [];
      newBreadcrumbs = [...breadcrumbs];
      newBreadcrumbs[newBreadcrumbs.length - 1].name = workspace.name;
      yield put({ type: 'app/breadcrumbs/SET', breadcrumbs: [...newBreadcrumbs] });

      // and navigate to workspace panel
      const workspacePanelPath = getRouteByPath(ROUTES.workspace.path, {
        projectId,
        workspaceId,
      });
      const navigate: NavigateFunction = action.data.navigate
      navigate(workspacePanelPath);
    }
  } catch (error: any) {
    const toast = {
      text: t('FAILED_TO', 1, {
        action: t('LOAD', 1, { thing: t('WORKSPACE') }),
      }),
      operationId: error.operationId,
    };
    yield put({ type: 'workspace/LOAD_FAILED' });
    yield put({ type: 'toast/ADD/ERROR', toast });
  } finally {
    yield put({ type: WorkspaceActionType.WORKSPACE_UPDATING, updating: false });
  }
}
